/**
 * Warlock kit identity (plan 29 F9, lane BZ 2026-10-11).
 *
 * Before this lane the Warlock auto shared the Mage auto's `arcane_orbit`
 * muzzle; Corruption and Curse of Agony landed the same `shadow_cloud`; Death
 * Coil and Howl of Terror both played `shadow_burst`; Immolate's fire cast
 * wound up in a purple `shadow_burst`; Life Tap and Drain Life both puffed the
 * same `preset_shadow_cloud`; Howl's 2 s wind-up showed nothing. Each def here
 * is named by the ability's TOML (`cast_effect` / `impact_effect`) so the
 * local module, the confirmed impact and a remote replay read the same id.
 *
 * Palette (TOML `aura_color`, ≥ 40 RGB apart inside the kit): auto lilac,
 * Corruption sickly olive, Curse of Agony magenta-crimson, Immolate fel
 * orange, Death Coil deep violet, Drain Life life green, Life Tap blood red,
 * Howl of Terror dusk indigo, Pact Door fel green. One-shot bursts end. No
 * screen shake or flash (the hit tier owns those).
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, shardBurst, glintScatter, groundCloud, starFlash, wispRise, slashArc } from './burstBuilders';

const LILAC = 0xb48ce0;
const LILAC_CORE = 0xf0e2ff;
const VOID = 0x1a0b2a;
const OLIVE = 0x8a9a2a;
const OLIVE_PALE = 0xd2e070;
const MAGENTA = 0xd4247a;
const MAGENTA_PALE = 0xff9ccc;
const FEL_ORANGE = 0xff6a00;
const FEL_GOLD = 0xffc04d;
const FEL_GREEN = 0x9cff3a;
const DEEP_VIOLET = 0x6a1fb0;
const VIOLET_PALE = 0xc79cff;
const INDIGO = 0x3d3f8f;
const INDIGO_PALE = 0x9fa3ff;

/** Warlock auto's hand flick: a lilac sputter around a dark core. */
const SHADOW_BOLT_CAST: VFXEffectConfig = {
  id: 'shadow_bolt_cast',
  name: 'Shadow Bolt Cast',
  category: 'burst',
  description: 'A dark core sputters at the hand inside a lilac flare as the shadow bolt leaves',
  primitives: [
    { type: 'circle', radius: 6, color: VOID, alpha: 0.9, depth: 97,
      stroke: { color: LILAC, width: 2 },
      tween: { alpha: 0, scale: 2, duration: 180, ease: 'Power2' } },
    glintScatter({ count: 4, spread: 10, color: LILAC, durationMs: 170 }),
  ],
};

/** Warlock auto's hit: a lilac crack — a ring and four dark splinters. */
const SHADOW_BOLT_CRACK: VFXEffectConfig = {
  id: 'shadow_bolt_crack',
  name: 'Shadow Bolt Crack',
  category: 'burst',
  description: 'A lilac ring cracks on the target and four dark splinters fly off a pale core — light and quick',
  primitives: [
    ringBlast({ radius: 9, color: LILAC, alpha: 0.6, stroke: { color: LILAC_CORE, width: 2 }, expandTo: 2, durationMs: 220 }),
    shardBurst({ count: 4, spread: 8, color: VOID, shard: { points: [0, -2, 9, 0, 0, 2] }, travel: 22, durationMs: 220, stroke: { color: LILAC, width: 1 } }),
    { type: 'circle', radius: 4, color: LILAC_CORE, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scale: 1.8, duration: 140, ease: 'Power1' } },
  ],
};

/** Gloammark's 2 s cast: a ring of motes and a swelling olive seed held
 *  over the warlock's head. Drawn over the sprite and kept outside the body
 *  (a tell that converges INTO the body hides behind it), symmetric because a
 *  wind-up spawn carries no facing. */
const CORRUPTION_GATHER: VFXEffectConfig = {
  id: 'corruption_gather',
  name: 'Gloammark Gather',
  category: 'burst',
  description: 'Olive motes and a pale ring gather around the warlock while a dark seed swells overhead to form Gloammark',
  primitives: [
    { type: 'arc', arcRadius: 58, arcStartAngle: -180, arcEndAngle: 180, width: 2, color: OLIVE_PALE, alpha: 0.75, depth: 101,
      tween: { scale: 0.8, duration: 1900 } },
    { type: 'circle', radius: 3, color: OLIVE_PALE, alpha: 0.95, depth: 101, glow: true,
      scatter: { count: 8, radius: 58 },
      tween: { scale: 0.7, duration: 1900 } },
    { type: 'circle', radius: 6, offsetY: -50, color: VOID, alpha: 0.95, depth: 102,
      stroke: { color: OLIVE_PALE, width: 2 },
      tween: { scale: 2.1, duration: 1900 } },
  ],
};

/** Gloammark's hit: olive motes burst as the mark takes hold. */
const CORRUPTION_ROT: VFXEffectConfig = {
  id: 'corruption_rot',
  name: 'Gloammark Contact',
  category: 'burst',
  description: 'Olive fragments scatter from a dark imprint as Gloammark takes hold on the target',
  primitives: [
    groundCloud({ width: 30, height: 14, color: VOID, alpha: 0.55, offsetY: 4, swellTo: 1.4, durationMs: 520, stroke: { color: OLIVE, width: 1.5 } }),
    shardBurst({ count: 7, spread: 9, color: OLIVE_PALE, shard: { width: 3, height: 3 }, travel: 26, durationMs: 380, glow: true }),
    wispRise({ count: 4, spread: 12, radius: 2.5, color: OLIVE, direction: 'down', travel: 14, durationMs: 460 }),
  ],
};

/** Last Toll's cast: a magenta brand flares at the hand. */
const AGONY_CAST: VFXEffectConfig = {
  id: 'agony_cast',
  name: 'Last Toll Cast',
  category: 'burst',
  description: 'A small five-point magenta brand flares at the hand and snaps shut',
  primitives: [
    starFlash({ points: 5, innerRadius: 4, outerRadius: 11, color: MAGENTA, spinRad: -0.6, expandTo: 1.5, durationMs: 200, glow: true }),
    { type: 'circle', radius: 3, color: MAGENTA_PALE, alpha: 1, depth: 98, glow: true,
      tween: { alpha: 0, scale: 2, duration: 160, ease: 'Power1' } },
  ],
};

/** Last Toll's hit: a magenta sigil branded on the target, pulsing twice. */
const AGONY_SIGIL: VFXEffectConfig = {
  id: 'agony_sigil',
  name: 'Last Toll Brand',
  category: 'burst',
  description: 'A magenta rune ring is branded on the target with a five-point star inside; it pulses twice and fades',
  primitives: [
    { type: 'circle', radius: 15, color: MAGENTA, alpha: 0.12, depth: 96,
      stroke: { color: MAGENTA, width: 2 },
      tween: { alpha: 0.5, scale: 1.12, duration: 160, yoyo: true, repeat: 1, ease: 'Sine.easeInOut' } },
    { type: 'star', points: 5, innerRadius: 5, outerRadius: 12, color: MAGENTA_PALE, alpha: 0.85, depth: 97,
      tween: { alpha: 0, rotation: -0.5, duration: 620, ease: 'Sine.easeOut' } },
    glintScatter({ count: 5, spread: 15, color: MAGENTA_PALE, durationMs: 240, delayMs: 300 }),
  ],
};

/** Cinder Scar's 600 ms cast: flame kindles over the warlock's head (drawn
 *  over the sprite; symmetric — a wind-up spawn carries no facing). */
const IMMOLATE_KINDLE: VFXEffectConfig = {
  id: 'immolate_kindle',
  name: 'Cinder Scar Kindle',
  category: 'burst',
  description: 'Fel-orange flame tongues lick up round a gold core that brightens over the warlock\'s head through the cast',
  primitives: [
    { type: 'circle', radius: 6, offsetY: -50, color: FEL_GOLD, alpha: 0.7, depth: 102, glow: true,
      tween: { scale: 2.2, alpha: 1, duration: 560 } },
    { type: 'polygon', polygonPoints: [0, -14, 4, -2, 0, 3, -4, -2], offsetY: -50, color: FEL_ORANGE, alpha: 0.9, depth: 101,
      scatter: { count: 4, radius: 14 },
      tween: { y: -8, alpha: 0.3, scale: 1.3, duration: 560, ease: 'Sine.easeOut' } },
    { type: 'circle', radius: 2, offsetY: -50, color: FEL_GREEN, alpha: 0.85, depth: 102,
      scatter: { count: 4, radius: 18 },
      tween: { y: -16, alpha: 0, duration: 560, ease: 'Sine.easeOut' } },
  ],
};

/** Cinder Scar's hit: flame tongues rise around the target. */
const IMMOLATE_ENGULF: VFXEffectConfig = {
  id: 'immolate_engulf',
  name: 'Cinder Scar Contact',
  category: 'burst',
  description: 'Fel-orange flame tongues rise all round the target from a gold flash, a fel-green ember lifting off',
  primitives: [
    { type: 'circle', radius: 9, color: FEL_GOLD, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scale: 2, duration: 180, ease: 'Power2' } },
    { type: 'polygon', polygonPoints: [0, -18, 5, -3, 0, 4, -5, -3], color: FEL_ORANGE, alpha: 0.9, depth: 98,
      scatter: { count: 6, radius: 14 },
      tween: { y: -14, alpha: 0, scale: 1.25, duration: 420, ease: 'Sine.easeOut' } },
    wispRise({ count: 3, spread: 12, radius: 2, color: FEL_GREEN, alpha: 0.7, travel: 26, durationMs: 480 }),
  ],
};

/** Death Coil's hand flash: a violet spiral flicks out. */
const DEATH_COIL_CAST: VFXEffectConfig = {
  id: 'death_coil_cast',
  name: 'Death Coil Cast',
  category: 'burst',
  description: 'Two deep-violet arcs whirl round the hand and fling the coil',
  primitives: [
    slashArc({ radius: 12, startDeg: -150, endDeg: 0, width: 3, color: DEEP_VIOLET, durationMs: 200, glow: true }),
    slashArc({ radius: 12, startDeg: 30, endDeg: 180, width: 3, color: VIOLET_PALE, durationMs: 200, fadeDelayMs: 30 }),
  ],
};

/** Death Coil's hit: the coil collapses inward and life motes return. */
const DEATH_COIL_BURST: VFXEffectConfig = {
  id: 'death_coil_burst',
  name: 'Death Coil Burst',
  category: 'burst',
  description: 'A deep-violet ring slams shut on a black core with three pale arcs whirling inward — the coil collapsing on its victim',
  primitives: [
    { type: 'circle', radius: 26, color: DEEP_VIOLET, alpha: 0.2, depth: 95,
      stroke: { color: VIOLET_PALE, width: 3 },
      tween: { scale: 0.25, alpha: 0.9, duration: 220, ease: 'Quad.easeIn' } },
    { type: 'circle', radius: 8, color: VOID, alpha: 0.95, depth: 97,
      stroke: { color: DEEP_VIOLET, width: 2 },
      tween: { alpha: 0, scale: 2.3, duration: 360, delay: 200, ease: 'Power2' } },
    slashArc({ radius: 18, startDeg: -90, endDeg: 30, width: 3, color: VIOLET_PALE, durationMs: 260, glow: true }),
    slashArc({ radius: 18, startDeg: 30, endDeg: 150, width: 3, color: VIOLET_PALE, durationMs: 260, glow: true }),
    slashArc({ radius: 18, startDeg: 150, endDeg: 270, width: 3, color: VIOLET_PALE, durationMs: 260, glow: true }),
  ],
};

/** Howl of Terror's 2 s wind-up: dread gathers round the warlock — drawn over
 *  the sprite and kept outside the body so all two seconds read as a tell
 *  (rings that closed into the body hid behind it), symmetric because a
 *  wind-up spawn carries no facing. */
const HOWL_OF_TERROR_GATHER: VFXEffectConfig = {
  id: 'howl_of_terror_gather',
  name: 'Howl of Terror Gather',
  category: 'burst',
  description: 'A bright indigo ring tightens round the warlock and a second one pulses inside it, ringed by dread motes, while a dark glow swells over its head: the howl is coming',
  primitives: [
    // Outlines are stroke-only arcs at a high alpha: a shape's alpha scales its
    // stroke too, and a faint ring vanished under the night scene's grade.
    { type: 'arc', arcRadius: 64, arcStartAngle: -180, arcEndAngle: 180, width: 3, color: INDIGO_PALE, alpha: 0.85, depth: 101, glow: true,
      tween: { scale: 0.8, duration: 1950 } },
    { type: 'arc', arcRadius: 50, arcStartAngle: -180, arcEndAngle: 180, width: 2, color: INDIGO_PALE, alpha: 0.55, depth: 101,
      tween: { scale: 1.12, alpha: 0.95, duration: 480, yoyo: true, repeat: 1, delay: 950 } },
    { type: 'circle', radius: 3.5, color: INDIGO_PALE, alpha: 0.95, depth: 102, glow: true,
      scatter: { count: 8, radius: 74 },
      tween: { scale: 0.7, duration: 1950 } },
    { type: 'circle', radius: 9, offsetY: -52, color: INDIGO, alpha: 0.9, depth: 102,
      stroke: { color: INDIGO_PALE, width: 2 },
      tween: { scale: 1.9, duration: 1950 } },
  ],
};

/** Howl of Terror's wave: three sound rings roll out through the wedge. */
const HOWL_OF_TERROR_WAVE: VFXEffectConfig = {
  id: 'howl_of_terror_wave',
  name: 'Howl of Terror Wave',
  category: 'burst',
  description: 'Three dusk-indigo sound arcs roll out from the warlock across the cone, one after another, fading at its edge',
  primitives: [
    ...[0, 90, 180].map((delay) => ({
      type: 'arc' as const, arcRadius: 30, arcStartAngle: -60, arcEndAngle: 60, width: 2,
      color: INDIGO_PALE, alpha: 0.9, depth: 95, glow: true,
      tween: { scale: 3.3, alpha: 0, duration: 460, delay, ease: 'Quad.easeOut' },
    })),
  ],
};

/** Howl of Terror's hit: fear grips — the target is ringed in shaking indigo. */
const TERROR_GRIP: VFXEffectConfig = {
  id: 'terror_grip',
  name: 'Terror Grip',
  category: 'burst',
  description: 'An indigo ring clamps onto the target and three pale fear marks flicker above it',
  primitives: [
    { type: 'circle', radius: 22, color: INDIGO, alpha: 0.15, depth: 95,
      stroke: { color: INDIGO_PALE, width: 2.5 },
      tween: { scale: 0.6, alpha: 0.8, duration: 200, ease: 'Quad.easeIn' } },
    { type: 'polygon', polygonPoints: [0, -6, 2, 2, 0, 6, -2, 2], offsetY: -30, color: INDIGO_PALE, alpha: 0.95, depth: 98, glow: true,
      scatter: { count: 3, radius: 9 },
      tween: { alpha: 0, y: -8, duration: 520, delay: 120, ease: 'Sine.easeOut' } },
    ringBlast({ radius: 14, color: INDIGO, alpha: 0.4, expandTo: 2, durationMs: 300, delayMs: 200 }),
  ],
};

/** Server-owned hold: static indigo bands stay readable with reduced motion. */
const HOWL_OF_TERROR_STATE: VFXEffectConfig = {
  id: 'howl_of_terror_state',
  name: 'Terror Hold',
  category: 'status',
  description: 'Dusk-indigo bands hold the target in place, with three pale fear marks above it',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 62, height: 16, offsetY: 20, color: INDIGO, alpha: 0.3, depth: 101,
      stroke: { color: INDIGO_PALE, width: 2.5 } },
    ...[-1, 1].map(side => ({
      type: 'polygon' as const, polygonPoints: [0, -12, 6 * side, -6, 6 * side, 12, 0, 17, -3 * side, 8],
      offsetX: side * 29, offsetY: 5, color: INDIGO, alpha: 0.85, depth: 101,
      stroke: { color: INDIGO_PALE, width: 2 },
    })),
    ...[-12, 0, 12].map(x => ({
      type: 'polygon' as const, polygonPoints: [0, -6, 2, 1, 0, 5, -2, 1],
      offsetX: x, offsetY: -42, color: INDIGO_PALE, alpha: 0.95, depth: 102,
    })),
  ],
};

export const WARLOCK_KIT_VFX: VFXEffectConfig[] = [
  SHADOW_BOLT_CAST, SHADOW_BOLT_CRACK,
  CORRUPTION_GATHER, CORRUPTION_ROT,
  AGONY_CAST, AGONY_SIGIL,
  IMMOLATE_KINDLE, IMMOLATE_ENGULF,
  DEATH_COIL_CAST, DEATH_COIL_BURST,
  HOWL_OF_TERROR_GATHER, HOWL_OF_TERROR_WAVE, TERROR_GRIP, HOWL_OF_TERROR_STATE,
];
